/**
 * Reference lists (M2 in the PRD): one description per list, used by the server
 * to validate and store, and by the web app to draw tables and forms.
 */
export const SHIFT_OPTIONS = [
    { value: 'morning', label: 'Morning' },
    { value: 'afternoon', label: 'Afternoon' },
    { value: 'full_day', label: 'Full day' },
    { value: 'half_day', label: 'Half day (any)' },
    { value: 'evening', label: 'Evening' },
    { value: 'any', label: 'Any time' },
];
export const TOUR_KIND_OPTIONS = [
    { value: 'shared', label: 'Shared' },
    { value: 'private', label: 'Private' },
    { value: 'cruise', label: 'Cruise excursion' },
    { value: 'transfer', label: 'Transfer' },
    { value: 'disposal', label: 'Vehicle at disposal' },
];
export const DIRECTION_OPTIONS = [
    { value: 'MC', label: 'East (Monaco side)' },
    { value: 'CA', label: 'West (Cannes side)' },
];
export const CHANNEL_TYPE_OPTIONS = [
    { value: 'ota', label: 'OTA / platform' },
    { value: 'agency', label: 'Agency' },
    { value: 'direct', label: 'Direct' },
];
export const GUIDE_STATUS_OPTIONS = [
    { value: 'staff', label: 'Staff' },
    { value: 'extra', label: 'Extra / stand-by' },
];
export const RESOURCES = {
    tours: {
        name: 'tours',
        table: 'tours',
        label: 'Tours',
        singular: 'tour',
        titleField: 'code',
        officeCanCreate: false,
        orderBy: "active DESC, array_position(ARRAY['shared','private','cruise','disposal','transfer'], kind), substring(code from '^[A-Z-]*'), nullif(substring(code from '[0-9]+'), '')::int NULLS FIRST, code",
        unique: ['code'],
        description: 'Every product the office can plan: shared and private tours, cruise excursions, transfers.',
        fields: [
            { name: 'code', column: 'code', label: 'Code', type: 'text', required: true, upper: true, maxLength: 20, inTable: true },
            { name: 'name', column: 'name', label: 'Name', type: 'text', required: true, maxLength: 160, inTable: true },
            { name: 'kind', column: 'kind', label: 'Type', type: 'enum', required: true, options: TOUR_KIND_OPTIONS, inTable: true, default: 'shared' },
            { name: 'shift', column: 'shift', label: 'Shift', type: 'enum', required: true, options: SHIFT_OPTIONS, inTable: true, default: 'full_day' },
            { name: 'minPax', column: 'min_pax', label: 'Minimum pax', type: 'int', required: true, min: 1, max: 60, inTable: true, default: 3, help: 'Below this number the departure is flagged (it can be changed for one day on the planning board).' },
            { name: 'color', column: 'color', label: 'Colour', type: 'color', inTable: true, default: '#9aa3b0', help: 'Shown as a light background on the planning board and the bookings list. Similar tours share a colour.' },
            { name: 'dedicatedVehicle', column: 'dedicated_vehicle', label: 'Needs a vehicle alone', type: 'bool', inTable: true, default: false, help: 'Private and cruise tours never share a vehicle.' },
            { name: 'startTime', column: 'start_time', label: 'Start', type: 'time' },
            { name: 'endTime', column: 'end_time', label: 'End', type: 'time' },
            { name: 'directionMorning', column: 'direction_morning', label: 'Morning direction', type: 'enum', options: DIRECTION_OPTIONS },
            { name: 'directionAfternoon', column: 'direction_afternoon', label: 'Afternoon direction', type: 'enum', options: DIRECTION_OPTIONS },
            { name: 'baseCode', column: 'base_code', label: 'Base code', type: 'text', upper: true, maxLength: 20, help: 'Same circuit in another shift (T2 for T2M and T2A).' },
            { name: 'family', column: 'family', label: 'Family', type: 'text', upper: true, maxLength: 30, help: 'Group used by the rules (T1, HALF, OTHER_FULL, PRIVATE).' },
            { name: 'departsFrom', column: 'departs_from', label: 'Departs from', type: 'text', maxLength: 60, default: 'Nice' },
            { name: 'itinerary', column: 'itinerary', label: 'Itinerary', type: 'longtext', maxLength: 2000 },
            { name: 'stops', column: 'stops', label: 'Stops', type: 'longtext', maxLength: 2000 },
            { name: 'notes', column: 'notes', label: 'Notes', type: 'longtext', maxLength: 2000 },
        ],
    },
    tourAliases: {
        name: 'tourAliases',
        table: 'tour_aliases',
        label: 'Tour names',
        singular: 'tour name',
        titleField: 'alias',
        officeCanCreate: true,
        orderBy: 'active DESC, alias',
        description: 'Old codes and the words agencies use for a tour ("1/2 MONACO" is T2A). Imports read this list.',
        fields: [
            { name: 'alias', column: 'alias', label: 'Name or old code', type: 'text', required: true, upper: true, maxLength: 160, inTable: true },
            { name: 'tourId', column: 'tour_id', label: 'Tour', type: 'ref', ref: 'tours', required: true, inTable: true },
            { name: 'agencyId', column: 'agency_id', label: 'Only for agency', type: 'ref', ref: 'agencies', inTable: true, help: 'Empty = valid for every source.' },
            { name: 'notes', column: 'notes', label: 'Notes', type: 'text', maxLength: 300 },
        ],
    },
    compatRules: {
        name: 'compatRules',
        table: 'compat_rules',
        label: 'Planning rules',
        singular: 'rule',
        titleField: 'leftRef',
        officeCanCreate: false,
        orderBy: 'active DESC, priority DESC, left_ref',
        description: 'Which tours may share a vehicle on the same day. A tour code, or a family written FAMILY:NAME.',
        fields: [
            { name: 'leftRef', column: 'left_ref', label: 'Tour or family', type: 'text', required: true, upper: true, maxLength: 40, inTable: true },
            { name: 'rightRef', column: 'right_ref', label: 'With', type: 'text', required: true, upper: true, maxLength: 40, inTable: true },
            { name: 'allowed', column: 'allowed', label: 'Allowed', type: 'bool', inTable: true, default: false },
            { name: 'priority', column: 'priority', label: 'Priority', type: 'int', min: 0, max: 99, inTable: true, default: 10, help: 'Higher wins when two rules apply.' },
            { name: 'note', column: 'note', label: 'Explanation', type: 'text', maxLength: 300, inTable: true },
        ],
    },
    vehicles: {
        name: 'vehicles',
        table: 'vehicles',
        label: 'Vehicles',
        singular: 'vehicle',
        titleField: 'plate',
        officeCanCreate: false,
        orderBy: 'active DESC, model, plate',
        unique: ['plate'],
        description: 'The physical fleet, by plate. V1, V2… are the vans scheduled on a day: they are set on the planning board, not here.',
        fields: [
            { name: 'plate', column: 'plate', label: 'Plate', type: 'text', required: true, upper: true, maxLength: 20, inTable: true },
            { name: 'model', column: 'model', label: 'Model', type: 'text', maxLength: 60, inTable: true },
            { name: 'seats', column: 'seats', label: 'Passenger seats', type: 'int', required: true, min: 1, max: 60, inTable: true, default: 8 },
            { name: 'notes', column: 'notes', label: 'Notes', type: 'text', maxLength: 300, inTable: true },
        ],
    },
    guides: {
        name: 'guides',
        table: 'guides',
        label: 'Guides',
        singular: 'guide',
        titleField: 'name',
        officeCanCreate: false,
        orderBy: 'active DESC, name',
        unique: ['name'],
        description: 'The people who drive and guide. Who drove which van is recorded day by day on the planning board.',
        fields: [
            { name: 'name', column: 'name', label: 'Name', type: 'text', required: true, maxLength: 80, inTable: true },
            { name: 'status', column: 'status', label: 'Status', type: 'enum', options: GUIDE_STATUS_OPTIONS, required: true, inTable: true, default: 'staff' },
            { name: 'languages', column: 'languages', label: 'Languages', type: 'tags', inTable: true, help: 'Language codes, e.g. FR, EN.' },
            { name: 'phone', column: 'phone', label: 'Phone', type: 'text', maxLength: 30 },
            { name: 'notes', column: 'notes', label: 'Notes', type: 'text', maxLength: 300 },
        ],
    },
    brands: {
        name: 'brands',
        table: 'brands',
        label: 'Brands',
        singular: 'brand',
        titleField: 'name',
        officeCanCreate: false,
        orderBy: 'active DESC, name',
        unique: ['code'],
        description: 'Same tours, vans and guides; the brand only labels the booking.',
        fields: [
            { name: 'code', column: 'code', label: 'Code', type: 'text', required: true, upper: true, maxLength: 10, inTable: true },
            { name: 'name', column: 'name', label: 'Name', type: 'text', required: true, maxLength: 80, inTable: true },
            { name: 'color', column: 'color', label: 'Colour', type: 'color', inTable: true, default: '#0f5fa8' },
        ],
    },
    channels: {
        name: 'channels',
        table: 'channels',
        label: 'Channels',
        singular: 'channel',
        titleField: 'code',
        officeCanCreate: true,
        orderBy: 'active DESC, code',
        unique: ['code'],
        description: 'Where a booking was sold. Anyone can add a new code when a new provider appears.',
        fields: [
            { name: 'code', column: 'code', label: 'Code', type: 'text', required: true, upper: true, maxLength: 12, inTable: true },
            { name: 'name', column: 'name', label: 'Name', type: 'text', required: true, maxLength: 80, inTable: true },
            { name: 'type', column: 'type', label: 'Type', type: 'enum', options: CHANNEL_TYPE_OPTIONS, required: true, inTable: true, default: 'ota' },
            { name: 'brandId', column: 'brand_id', label: 'Brand', type: 'ref', ref: 'brands', inTable: true },
            { name: 'notes', column: 'notes', label: 'Notes', type: 'text', maxLength: 300 },
        ],
    },
    agencies: {
        name: 'agencies',
        table: 'agencies',
        label: 'Agencies',
        singular: 'agency',
        titleField: 'name',
        officeCanCreate: true,
        orderBy: 'active DESC, name',
        unique: ['name'],
        description: 'Partners who send bookings by email or Excel. Their import layout is saved here later.',
        fields: [
            { name: 'name', column: 'name', label: 'Name', type: 'text', required: true, maxLength: 120, inTable: true },
            { name: 'channelId', column: 'channel_id', label: 'Channel', type: 'ref', ref: 'channels', inTable: true },
            { name: 'emails', column: 'emails', label: 'Sender emails or domains', type: 'tags', inTable: true, help: 'Used to recognise their emails, e.g. carol-voyages.com.' },
            { name: 'contact', column: 'contact', label: 'Contact', type: 'text', maxLength: 200 },
            { name: 'notes', column: 'notes', label: 'Notes', type: 'longtext', maxLength: 2000 },
        ],
    },
    hotels: {
        name: 'hotels',
        table: 'hotels',
        label: 'Hotels',
        singular: 'hotel',
        titleField: 'name',
        officeCanCreate: true,
        orderBy: 'active DESC, name',
        unique: ['name'],
        description: 'Pickup addresses. A booking with a known hotel gets its address automatically.',
        fields: [
            { name: 'name', column: 'name', label: 'Name', type: 'text', required: true, maxLength: 160, inTable: true },
            { name: 'address', column: 'address', label: 'Address', type: 'text', maxLength: 300, inTable: true },
            { name: 'town', column: 'town', label: 'Town', type: 'text', maxLength: 80, inTable: true },
            { name: 'pickupPoint', column: 'pickup_point', label: 'Pickup point', type: 'text', maxLength: 200, help: 'Where the guide waits, if not the hotel door.' },
            { name: 'aliases', column: 'aliases', label: 'Other spellings', type: 'tags', help: 'Names used by platforms or agencies for the same hotel.' },
        ],
    },
    languages: {
        name: 'languages',
        table: 'languages',
        label: 'Languages',
        singular: 'language',
        titleField: 'code',
        officeCanCreate: true,
        orderBy: 'active DESC, sort_order, code',
        unique: ['code'],
        description: 'Tour languages. Anyone can add one when a booking needs it.',
        fields: [
            { name: 'code', column: 'code', label: 'Code', type: 'text', required: true, upper: true, maxLength: 5, inTable: true },
            { name: 'label', column: 'label', label: 'Name', type: 'text', required: true, maxLength: 40, inTable: true },
            { name: 'bdrCode', column: 'bdr_code', label: 'Code on the BDR', type: 'text', upper: true, maxLength: 5, inTable: true, help: 'Leave empty to print the code (Spanish prints SP).' },
            { name: 'color', column: 'color', label: 'Colour', type: 'color', inTable: true, default: '#7d8a99', help: 'Background of the language badge in Bookings and Planning.' },
            { name: 'sortOrder', column: 'sort_order', label: 'Order', type: 'int', min: 0, max: 999, default: 100 },
        ],
    },
};
export const RESOURCE_ORDER = [
    'tours',
    'tourAliases',
    'compatRules',
    'vehicles',
    'guides',
    'channels',
    'agencies',
    'hotels',
    'languages',
    'brands',
];
//# sourceMappingURL=reference.js.map